import _ from "lodash";
import {z} from "zod";
import {zodResolver} from "@hookform/resolvers/zod";
import {useForm, UseFormReturn} from "react-hook-form";
import {Grid} from "@mui/material";
import {swallowException} from "../../utils/appUtils";
import {getResourceErrorMessage} from "../../utils/resourceUtils";
import {User, UserGlobalRole} from "../../types/domain/user";
import {createUser, deleteUser, patchUser, PatchUserBodyType, PostUserBodyType} from "../../services/userService";
import {Dialog} from "../base/Dialog";
import {UserInfoForm, UserInfoFormInputs} from "../users/UserInfoForm";
import {UserAccountForm, UserAccountFormInputs} from "../users/UserAccountForm";
import {UserInterfaceForm, UserInterfaceFormInputs} from "../users/UserInterfaceForm";
import {UserEmailPreferencesForm, UserEmailPreferencesFormInputs} from "../users/UserEmailPreferencesForm";
import {DEFAULT_USER_PREFERENCES} from "../../utils/userUtils";
import {LanguageType} from "../../types/domain/languageType";
import {MeasureSystemType} from "../../types/domain/measureSystemType";
import {useResourceManagement} from "../../hooks/resourceHooks";
import {useTranslation} from "react-i18next";
import {UserIcon} from "../icons/UserIcon";
import {DateFormatType} from "../../types/domain/dateFormat";

export interface Props {
    mode: "create" | "edit" | "settings";
    user?: User;
    open: boolean;
    onClose: (isSuccessful: boolean) => void;
}

export const UserEditDialog: React.FC<Props> = ({open, onClose, mode, user}) => {
    const {t} = useTranslation();
    const userManagement = useResourceManagement({createUser, patchUser, deleteUser});
    const initFormValues = mode === "create" ? NEW_USER_FORM_INPUTS : userToFormInputs(user!);
    const form = useForm({
        defaultValues: initFormValues,
        resolver: zodResolver(USER_EDIT_SCHEMA),
    });

    const title =
        mode === "create"
            ? t("dialogs.user_edit.create_title")
            : mode === "edit"
              ? t("dialogs.user_edit.edit_title")
              : t("dialogs.user_edit.user_title");

    const onSubmit = (formInputs: FormInputs) => {
        userManagement.reset();

        const apiCall =
            mode === "create"
                ? userManagement.createUser(formInputsToPostUserBody(formInputs), {suppressEmail: false})
                : userManagement.patchUser(user!.id, formInputsToPatchUserBody(formInputs, mode === "edit"));

        apiCall.then(() => onClose(true)).catch(swallowException);
    };

    const onDelete = () => {
        userManagement.reset();

        userManagement
            .deleteUser(user!.id)
            .then(() => onClose(true))
            .catch(swallowException);
    };

    return (
        <Dialog
            enableSubmit
            enableDelete={mode === "edit"}
            submitTitle={mode === "create" ? t("dialogs.actions.create") : t("dialogs.actions.save")}
            title={title}
            titleIcon={
                <UserIcon
                    sx={{
                        stroke: "#404DB8",
                        width: "1.125rem",
                        height: "1.125rem",
                    }}
                />
            }
            open={open}
            error={getResourceErrorMessage(userManagement.resource)}
            deleteConfirmation={t("dialogs.user_edit.delete_confirmation")}
            onClose={() => onClose(false)}
            onDelete={onDelete}
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={form.handleSubmit(onSubmit)}
        >
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <UserInfoForm
                        initFormValues={initFormValues}
                        form={form as unknown as UseFormReturn<UserInfoFormInputs>}
                    />
                </Grid>
                <Grid item xs={12}>
                    <UserInterfaceForm
                        initFormValues={initFormValues}
                        form={form as unknown as UseFormReturn<UserInterfaceFormInputs>}
                    />
                </Grid>
                <Grid item xs={12}>
                    <UserEmailPreferencesForm
                        initFormValues={initFormValues}
                        form={form as unknown as UseFormReturn<UserEmailPreferencesFormInputs>}
                    />
                </Grid>
                {mode !== "settings" && (
                    <Grid item xs={12}>
                        <UserAccountForm
                            initFormValues={initFormValues}
                            form={form as unknown as UseFormReturn<UserAccountFormInputs>}
                        />
                    </Grid>
                )}
            </Grid>
        </Dialog>
    );
};

interface FormInputs
    extends UserInfoFormInputs,
        UserAccountFormInputs,
        UserInterfaceFormInputs,
        UserEmailPreferencesFormInputs {}

const USER_EDIT_SCHEMA = z.object({
    firstName: z.string().min(1).max(200),
    lastName: z.string().min(1).max(200),
    email: z.string().email(),
    company: z.string(),
    country: z.string(),
    phone: z.string().max(200),
    globalRole: z.nativeEnum(UserGlobalRole),
    betaFeatures: z.boolean(),
    language: z.nativeEnum(LanguageType),
    measureSystem: z.nativeEnum(MeasureSystemType),
    dateFormat: z.nativeEnum(DateFormatType),
    enabledAdvancedFeatures: z.boolean(),
    enabledUiHelp: z.boolean(),
    enabledFieldAlerts: z.boolean(),
    enabledMarketingEmails: z.boolean(),
});

const formInputsToPatchUserBody = (inputs: FormInputs, patchSecurity: boolean) => {
    const user: PatchUserBodyType = {
        ...(patchSecurity && {
            security: {
                role: inputs.globalRole,
                attributes: {
                    ...(inputs.betaFeatures && {betaFeatures: true}),
                },
            },
        }),
        properties: {
            firstName: inputs.firstName,
            lastName: inputs.lastName,
            email: inputs.email,
            company: _.isEmpty(inputs.company) ? null : inputs.company,
            country: inputs.country,
            phone: _.isEmpty(inputs.phone) ? null : inputs.phone,
        },
        preferences: {
            language: inputs.language,
            measureSystem: inputs.measureSystem,
            dateFormat: inputs.dateFormat,
            enabledAdvancedFeatures: inputs.enabledAdvancedFeatures,
            enabledUiHelp: inputs.enabledUiHelp,
            enabledFieldAlerts: inputs.enabledFieldAlerts,
            enabledMarketingEmails: inputs.enabledMarketingEmails,
        },
    };
    return user;
};

const formInputsToPostUserBody = (inputs: FormInputs) => {
    const user: PostUserBodyType = {
        security: {
            role: inputs.globalRole,
            attributes: {
                ...(inputs.betaFeatures && {betaFeatures: true}),
            },
        },
        properties: {
            firstName: inputs.firstName,
            lastName: inputs.lastName,
            email: inputs.email,
            company: _.isEmpty(inputs.company) ? null : inputs.company,
            country: inputs.country,
            phone: _.isEmpty(inputs.phone) ? null : inputs.phone,
        },
        preferences: {
            language: inputs.language,
            measureSystem: inputs.measureSystem,
            dateFormat: inputs.dateFormat,
            enabledAdvancedFeatures: inputs.enabledAdvancedFeatures,
            enabledUiHelp: inputs.enabledUiHelp,
            enabledFieldAlerts: inputs.enabledFieldAlerts,
            enabledMarketingEmails: inputs.enabledMarketingEmails,
        },
        isActive: true,
        isApiEnabled: true,
    };
    return user;
};

const userToFormInputs = (user: User): FormInputs => ({
    firstName: user.properties.firstName,
    lastName: user.properties.lastName,
    email: user.properties.email,
    company: user.properties.company ?? "",
    globalRole: user.security.role,
    country: user.properties.country ?? "",
    phone: user.properties.phone ?? "",
    betaFeatures: user.security.attributes.betaFeatures ?? false,
    language: user.preferences.language ?? DEFAULT_USER_PREFERENCES.language,
    measureSystem: user.preferences.measureSystem ?? DEFAULT_USER_PREFERENCES.measureSystem,
    dateFormat: user.preferences.dateFormat ?? DEFAULT_USER_PREFERENCES.dateFormat,
    enabledAdvancedFeatures:
        user.preferences.enabledAdvancedFeatures ?? DEFAULT_USER_PREFERENCES.enabledAdvancedFeatures,
    enabledUiHelp: user.preferences.enabledUiHelp ?? DEFAULT_USER_PREFERENCES.enabledUiHelp,
    enabledFieldAlerts: user.preferences.enabledFieldAlerts ?? DEFAULT_USER_PREFERENCES.enabledFieldAlerts,
    enabledMarketingEmails: user.preferences.enabledMarketingEmails ?? DEFAULT_USER_PREFERENCES.enabledMarketingEmails,
});

const NEW_USER_FORM_INPUTS: FormInputs = {
    firstName: "",
    lastName: "",
    email: "",
    company: "",
    country: null,
    phone: "",
    globalRole: UserGlobalRole.USER,
    betaFeatures: false,
    language: DEFAULT_USER_PREFERENCES.language,
    measureSystem: DEFAULT_USER_PREFERENCES.measureSystem,
    dateFormat: DEFAULT_USER_PREFERENCES.dateFormat,
    enabledAdvancedFeatures: DEFAULT_USER_PREFERENCES.enabledAdvancedFeatures,
    enabledUiHelp: DEFAULT_USER_PREFERENCES.enabledUiHelp,
    enabledFieldAlerts: DEFAULT_USER_PREFERENCES.enabledFieldAlerts,
    enabledMarketingEmails: DEFAULT_USER_PREFERENCES.enabledMarketingEmails,
};
